type CacheData = {
  data: any;
  timestamp: number;
  expiry: number;
};

type CacheConfig = {
  maxAge?: number;
  maxSize?: number;
  prefix?: string;
};

class CacheManager {
  private cache: Map<string, CacheData> = new Map();
  private config: CacheConfig;
  private serviceWorker: ServiceWorker | null = null;

  constructor(config: CacheConfig = {}) {
    this.config = {
      maxAge: 3 * 60 * 60 * 1000,
      maxSize: 100,
      prefix: 'cloudee-cache',
      ...config,
    };
    this.initServiceWorker();
    this.startAutoCleanup();
  }

  private startAutoCleanup(): void {
    setInterval(() => {
      this.clearExpired();
    }, 30 * 60 * 1000);
  }

  private async initServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        this.serviceWorker = registration.active;
        
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        navigator.serviceWorker.addEventListener('message', this.handleSWMessage.bind(this));
      } catch (error) {
        console.warn('Service Worker registration failed:', error);
      }
    }
  }

  private handleSWMessage(event: MessageEvent) {
    const { type, urls, error } = event.data;
    
    switch (type) {
      case 'CACHE_SUCCESS':
        break;
      case 'CACHE_ERROR':
        console.error('Cache error:', error);
        break;
      case 'CACHE_CLEARED':
        this.cache.clear();
        localStorage.removeItem(`${this.config.prefix}-keys`);
        break;
    }
  }

  set(key: string, data: any, customMaxAge?: number): void {
    const maxAge = customMaxAge || this.config.maxAge!;
    const timestamp = Date.now();
    const expiry = timestamp + maxAge;

    if (this.cache.size >= this.config.maxSize!) {
      this.evictOldest();
    }

    this.cache.set(key, { data, timestamp, expiry });
    this.updateStorageKeys();
  }

  get(key: string): any | null {
    const item = this.cache.get(key);
    
    if (!item) return null;
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      this.updateStorageKeys();
      return null;
    }
    
    return item.data;
  }

  has(key: string): boolean {
    const item = this.cache.get(key);
    return item ? Date.now() <= item.expiry : false;
  }

  delete(key: string): boolean {
    const result = this.cache.delete(key);
    this.updateStorageKeys();
    return result;
  }

  clear(): void {
    this.cache.clear();
    this.updateStorageKeys();
    this.postMessageToSW({ type: 'CLEAR_CACHE' });
  }

  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.config.maxSize,
      keys: Array.from(this.cache.keys()),
      expired: this.getExpiredKeys().length,
    };
  }

  private evictOldest(): void {
    let oldestKey = '';
    let oldestTime = Date.now();
    
    this.cache.forEach((item, key) => {
      if (item.timestamp < oldestTime) {
        oldestTime = item.timestamp;
        oldestKey = key;
      }
    });
    
    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  private getExpiredKeys(): string[] {
    const now = Date.now();
    const expiredKeys: string[] = [];
    
    this.cache.forEach((item, key) => {
      if (now > item.expiry) {
        expiredKeys.push(key);
      }
    });
    
    return expiredKeys;
  }

  private updateStorageKeys(): void {
    try {
      const keys = Array.from(this.cache.keys());
      localStorage.setItem(`${this.config.prefix}-keys`, JSON.stringify(keys));
    } catch (error) {
      console.warn('Failed to update storage keys:', error);
    }
  }

  private postMessageToSW(message: any): void {
    if (this.serviceWorker) {
      this.serviceWorker.postMessage(message);
    }
  }

  async preloadAssets(urls: string[]): Promise<void> {
    this.postMessageToSW({ type: 'CACHE_URLS', urls });
  }

  clearExpired(): number {
    const now = Date.now();
    let removedCount = 0;
    
    this.cache.forEach((item, key) => {
      if (now > item.expiry) {
        this.cache.delete(key);
        removedCount++;
      }
    });
    
    if (removedCount > 0) {
      this.updateStorageKeys();
    }
    
    return removedCount;
  }
}

const globalCache = new CacheManager();

export const cacheUtils = {
  images: new CacheManager({ prefix: 'img-cache', maxAge: 3 * 60 * 60 * 1000, maxSize: 200 }),
  icons: new CacheManager({ prefix: 'icon-cache', maxAge: 3 * 60 * 60 * 1000, maxSize: 500 }),
  api: new CacheManager({ prefix: 'api-cache', maxAge: 3 * 60 * 60 * 1000, maxSize: 50 }),
  static: new CacheManager({ prefix: 'static-cache', maxAge: 3 * 60 * 60 * 1000, maxSize: 100 }),
  global: globalCache,
};

export const preloadCriticalAssets = async () => {
  const criticalAssets = [
    '/assets/img/logo.svg',
    '/assets/img/Logo.png',
    '/assets/img/white-logo.svg',
    '/assets/img/avatar.png',
    '/assets/img/profiles/male.png',
    'https://cloudee-sales-crm-artifact.s3.ap-southeast-7.amazonaws.com/public-artifacts/user/male.png',
  ];

  await globalCache.preloadAssets(criticalAssets);
};

export const getOptimizedImageUrl = (url: string, options: { width?: number; height?: number; quality?: number } = {}) => {
  if (!url) return '';
  
  if (url.includes('cloudee-sales-crm-artifact.s3')) {
    const params = new URLSearchParams();
    if (options.width) params.set('w', options.width.toString());
    if (options.height) params.set('h', options.height.toString());
    if (options.quality) params.set('q', options.quality.toString());
    
    return params.toString() ? `${url}?${params.toString()}` : url;
  }
  
  return url;
};

export const createImageWithFallback = (src: string, fallback: string = '/assets/img/avatar.png') => {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      cacheUtils.images.set(src, { loaded: true, timestamp: Date.now() });
      resolve(img);
    };
    
    img.onerror = () => {
      const fallbackImg = new Image();
      fallbackImg.onload = () => resolve(fallbackImg);
      fallbackImg.onerror = () => reject(new Error('Failed to load fallback image'));
      fallbackImg.src = fallback;
    };
    
    img.src = src;
  });
};

export const memoizeWithCache = <T extends (...args: any[]) => any>(
  fn: T,
  keyGenerator?: (...args: Parameters<T>) => string,
  maxAge?: number
): T => {
  return ((...args: Parameters<T>) => {
    const key = keyGenerator ? keyGenerator(...args) : JSON.stringify(args);
    
    if (globalCache.has(key)) {
      return globalCache.get(key);
    }
    
    const result = fn(...args);
    globalCache.set(key, result, maxAge);
    return result;
  }) as T;
};

export const clearExpiredCache = () => {
  let totalCleared = 0;
  Object.entries(cacheUtils).forEach(([type, cache]) => {
    const cleared = cache.clearExpired();
    totalCleared += cleared;
  });
  return totalCleared;
};

export const getCacheStatus = () => {
  return {
    images: cacheUtils.images.getStats(),
    icons: cacheUtils.icons.getStats(),
    api: cacheUtils.api.getStats(),
    static: cacheUtils.static.getStats(),
    global: cacheUtils.global.getStats(),
  };
};

export default cacheUtils; 